import { useState, useEffect } from "react";
import api from '../api/axios';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../context/LanguageContext";
import { FaFlask, FaCheck, FaTimes, FaUsers, FaPlus, FaTrash } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../styles/common.css";
import "./TraitementMasse.css";

export default function TraitementMasse() {
  const { t } = useTranslation();
  const [troupeaux, setTroupeaux] = useState([]);
  const [selectedTroupeau, setSelectedTroupeau] = useState("");
  const [animaux, setAnimaux] = useState([]);
  const [selectedAnimaux, setSelectedAnimaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    type_intervention: "",
    statut: "",
    date_intervention: "",
    description: "",
    prochaine_intervention: "",
  });
  const [stocksDisponibles, setStocksDisponibles] = useState([]);
  const [produitsUtilises, setProduitsUtilises] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({
    troupeau: "",
    animaux: "",
    type_intervention: "",
    date_intervention: "",
  });
  const [touchedFields, setTouchedFields] = useState({
    troupeau: false,
    animaux: false,
    type_intervention: false,
    date_intervention: false,
  });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [resTroupeaux, resAnimaux, resStocks] = await Promise.all([
          api.get("/api/troupeaux/"),
          api.get("/api/animaux/"),
          api.get("/api/stocks-pharmacie/disponibles/"),
        ]);
        setTroupeaux(resTroupeaux.data);
        setAnimaux(resAnimaux.data);
        setStocksDisponibles(resStocks.data);
        setLoading(false);
      } catch (err) {
        setError("Erreur lors du chargement des données.");
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fonction de validation des champs obligatoires
  const validateRequiredField = (name, value) => {
    if (!value || (typeof value === 'string' && value.trim() === "") || value === null || (Array.isArray(value) && value.length === 0)) {
      return "Ce champ est obligatoire";
    }
    return "";
  };

  // Validation d'un champ spécifique
  const validateField = (name, value) => {
    if (name === "troupeau" || name === "type_intervention" || name === "date_intervention") {
      return validateRequiredField(name, value);
    }
    if (name === "animaux") {
      if (!value || value.length === 0) {
        return "Veuillez sélectionner au moins un animal";
      }
    }
    return "";
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Validation en temps réel si le champ a été touché
    if (touchedFields[name] || fieldErrors[name]) {
      const error = validateField(name, value);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Marquer le champ comme touché
    setTouchedFields(prev => ({ ...prev, [name]: true }));
    
    // Valider le champ
    const error = validateField(name, value);
    setFieldErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleTroupeauChange = (e) => {
    const troupeauId = e.target.value;
    setSelectedTroupeau(troupeauId);
    setSelectedAnimaux([]);
    
    // Validation en temps réel si le champ a été touché
    if (touchedFields.troupeau || fieldErrors.troupeau) {
      const error = validateField("troupeau", troupeauId);
      setFieldErrors(prev => ({
        ...prev,
        troupeau: error
      }));
    }
    
    // Réinitialiser l'erreur des animaux si le troupeau change
    setFieldErrors(prev => ({
      ...prev,
      animaux: ""
    }));
  };

  const handleTroupeauBlur = () => {
    setTouchedFields(prev => ({ ...prev, troupeau: true }));
    const error = validateField("troupeau", selectedTroupeau);
    setFieldErrors(prev => ({
      ...prev,
      troupeau: error
    }));
  };

  const selectTroupeauAnimaux = () => {
    if (!selectedTroupeau) {
      toast.warning("Veuillez d'abord sélectionner un troupeau.");
      return;
    }
    
    const animauxDuTroupeau = animaux.filter(
      animal => String(animal.troupeau) === selectedTroupeau 
    );
    
    const allIds = animauxDuTroupeau.map(animal => String(animal.id));
    setSelectedAnimaux(allIds);
    
    // Réinitialiser l'erreur des animaux
    setFieldErrors(prev => ({
      ...prev,
      animaux: ""
    }));
  };

  const handleSelectAnimaux = e => {
    const options = Array.from(e.target.selectedOptions, option => option.value);
    setSelectedAnimaux(options);
    
    // Validation en temps réel si le champ a été touché
    if (touchedFields.animaux || fieldErrors.animaux) {
      const error = validateField("animaux", options);
      setFieldErrors(prev => ({
        ...prev,
        animaux: error
      }));
    }
  };

  const handleSelectAnimauxBlur = () => {
    setTouchedFields(prev => ({ ...prev, animaux: true }));
    const error = validateField("animaux", selectedAnimaux);
    setFieldErrors(prev => ({
      ...prev,
      animaux: error
    }));
  };

  const handleAddProduit = () => {
    setProduitsUtilises([...produitsUtilises, {
      stock_id: "",
      quantite_utilisee: "",
      notes: "",
    }]);
  };

  const handleRemoveProduit = (index) => {
    setProduitsUtilises(produitsUtilises.filter((_, i) => i !== index));
  };

  const handleProduitChange = (index, field, value) => {
    const updated = [...produitsUtilises];
    updated[index] = { ...updated[index], [field]: value };
    setProduitsUtilises(updated);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    
    // Marquer tous les champs obligatoires comme touchés
    const newTouchedFields = {
      troupeau: true,
      animaux: true,
      type_intervention: true,
      date_intervention: true,
    };
    setTouchedFields(newTouchedFields);
    
    // Valider tous les champs obligatoires
    const newFieldErrors = {
      troupeau: validateField("troupeau", selectedTroupeau),
      animaux: validateField("animaux", selectedAnimaux),
      type_intervention: validateField("type_intervention", formData.type_intervention),
      date_intervention: validateField("date_intervention", formData.date_intervention),
    };
    setFieldErrors(newFieldErrors);
    
    // Vérifier s'il y a des erreurs
    const hasErrors = Object.values(newFieldErrors).some(error => error !== "");
    
    if (hasErrors) {
      return; // Ne pas soumettre si il y a des erreurs
    }
    
    // Valider et filtrer les produits utilisés
    const produitsValides = [];
    const produitsInvalides = [];
    
    produitsUtilises.forEach((p, index) => {
      // Ignorer les produits sans stock_id sélectionné
      if (!p.stock_id || p.stock_id.trim() === "") {
        return;
      }
      
      // Valider la quantité utilisée
      const quantite = parseFloat(p.quantite_utilisee);
      if (!Number.isFinite(quantite) || quantite <= 0) {
        produitsInvalides.push({
          index: index + 1,
          produit: stocksDisponibles.find(s => s.id === parseInt(p.stock_id))?.produit_nom || "Produit inconnu",
          quantite: p.quantite_utilisee
        });
        return;
      }
      
      // Produit valide
      produitsValides.push({
        stock_id: p.stock_id,
        quantite_utilisee: quantite,
        notes: p.notes || "",
      });
    });
    
    // Bloquer la soumission si des produits sont invalides
    if (produitsInvalides.length > 0) {
      const errorMessages = produitsInvalides.map(
        inv => `Produit "${inv.produit}" (ligne ${inv.index}): quantité invalide "${inv.quantite}"`
      );
      toast.error(
        `Veuillez corriger les quantités invalides:\n${errorMessages.join("\n")}`,
        { autoClose: 5000 }
      );
      return;
    }
    
    const idsToSubmit = selectedAnimaux.map(id => parseInt(id));
    setSubmitting(true);
    try {
      const dataToSend = {
        ids_animaux: idsToSubmit,
        ...formData,
        produits_utilises: produitsValides,
      };
      await api.post("/api/traitement-masse/", dataToSend);
      toast.success(t.treatmentSuccess);
      setTimeout(() => {
        navigate("/sante");
      }, 500);
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message ||
                          Object.values(err.response?.data || {}).flat().join(", ") ||
                          t.treatmentError;
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="traitement-masse-page">
        <div className="loading-container">
          <p className="loading-text">Chargement des animaux...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="traitement-masse-page">
        <div className="title-badge">
          <FaFlask className="page-title-icon" aria-hidden="true" />
          <span>ERREUR</span>
        </div>
        <div className="error-container">
          <p className="error-message">{error}</p>
        </div>
      </div>
    );
  }

  const animauxDuTroupeauCourant = animaux.filter(
    animal => String(animal.troupeau) === selectedTroupeau
  );

  const isFullTroupeauSelected = selectedTroupeau && 
                                selectedAnimaux.length === animauxDuTroupeauCourant.length;

  return (
    <div className="traitement-masse-page">
      <div className="title-badge">
        <FaFlask className="page-title-icon" aria-hidden="true" />
        <span>{t.massTreatmentTitle}</span>
      </div>

      <form onSubmit={handleSubmit} className="traitement-masse-form">
        {/* Bannière d'information */}
        <div className="form-info-banner">
          <span className="info-icon">ℹ️</span>
          <div className="info-content">
            <strong>{t.requiredFields}</strong>
            <span className="required-indicator">*</span> {t.requiredFieldNote}
          </div>
        </div>

        {/* Section sélection du troupeau */}
        <div className="form-section-container">
          <div className="form full-width">
            <label htmlFor="troupeau">
              {t.chooseHerd} <span className="required-indicator">*</span>
            </label>
            <select 
              id="troupeau"
              name="troupeau"
              value={selectedTroupeau} 
              onChange={handleTroupeauChange}
              onBlur={handleTroupeauBlur}
              className={fieldErrors.troupeau && touchedFields.troupeau ? "input-error" : ""}
            >
              <option value="">{t.selectHerd}</option>
              {troupeaux.map(troupeau => (
                <option key={troupeau.id} value={String(troupeau.id)}>
                  {troupeau.nom}
                </option>
              ))}
            </select>
            {fieldErrors.troupeau && touchedFields.troupeau && (
              <span className="field-error-message">{fieldErrors.troupeau}</span>
            )}
          </div>

          {selectedTroupeau && (
            <div className="troupeau-actions">
              <button
                type="button"
                className="save-but"
                onClick={selectTroupeauAnimaux}
              >
                <FaUsers />
                <span>{t.fullHerd} ({animauxDuTroupeauCourant.length})</span>
              </button>
            </div>
          )}

          {selectedTroupeau && (
            <div className="selection-info-container">
              <p className="selection-info">
                {isFullTroupeauSelected 
                  ? t.herdSelected
                  : `${selectedAnimaux.length} ${t.animalsSelected}`}
              </p>
            </div>
          )}

          <div className="form full-width">
            <label htmlFor="animaux">
              {t.verifyAnimals} <span className="required-indicator">*</span>
            </label>
            <select 
              id="animaux"
              name="animaux"
              multiple 
              value={selectedAnimaux} 
              onChange={handleSelectAnimaux}
              onBlur={handleSelectAnimauxBlur}
              className={`multiple-select ${fieldErrors.animaux && touchedFields.animaux ? "input-error" : ""}`}
              disabled={!selectedTroupeau}
            >
              {animauxDuTroupeauCourant.map(animal => (
                <option key={animal.id} value={String(animal.id)}>
                  {animal.matricule}
                </option>
              ))}
            </select>
            <small className="select-hint">
              {t.selectMultipleHint}
            </small>
            {fieldErrors.animaux && touchedFields.animaux && (
              <span className="field-error-message">{fieldErrors.animaux}</span>
            )}
          </div>
        </div>

        {/* Section informations du traitement */}
        <div className="form-section-container">
          <div className="form">
            <label htmlFor="type_intervention">
              {t.interventionType} <span className="required-indicator">*</span>
            </label>
            <select 
              id="type_intervention"
              name="type_intervention" 
              value={formData.type_intervention} 
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.type_intervention && touchedFields.type_intervention ? "input-error" : ""}
            >
              <option value="">{t.selectOption}</option>
              <option value="vaccination">{t.vaccination}</option>
              <option value="traitement">{t.treatment}</option>
              <option value="vermifuge">{t.deworming}</option>
              <option value="autre">{t.other}</option>
            </select>
            {fieldErrors.type_intervention && touchedFields.type_intervention && (
              <span className="field-error-message">{fieldErrors.type_intervention}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="statut">{t.statusLabel}</label>
            <select
              id="statut"
              name="statut"
              value={formData.statut}
              onChange={handleChange}
            >
              <option value="">{t.selectOption}</option>
              <option value="traite">{t.treated}</option>
              <option value="non_traite">{t.notTreated}</option>
            </select>
          </div>

          <div className="form">
            <label htmlFor="date_intervention">
              {t.interventionDate} <span className="required-indicator">*</span>
            </label>
            <input 
              type="date" 
              id="date_intervention"
              name="date_intervention" 
              value={formData.date_intervention || ""} 
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.date_intervention && touchedFields.date_intervention ? "input-error" : ""}
            />
            {fieldErrors.date_intervention && touchedFields.date_intervention && (
              <span className="field-error-message">{fieldErrors.date_intervention}</span>
            )}
          </div>

          {/* Section Produits utilisés */}
          <div className="form full-width">
            <label>
              {t.productsUsed}
            </label>
            <div className="produits-utilises-container">
              {produitsUtilises.map((produit, index) => (
                <div key={index} className="produit-utilise-row">
                  <div className="produit-field">
                    <label htmlFor={`stock-${index}`}>{t.selectProduct}</label>
                  <select
                      id={`stock-${index}`}
                    value={produit.stock_id || ""}
                    onChange={(e) => handleProduitChange(index, "stock_id", e.target.value)}
                    className="produit-select"
                  >
                    <option value="">-- Sélectionner un stock --</option>
                    {stocksDisponibles.map((stock) => (
                      <option key={stock.id} value={stock.id}>
                        {stock.produit_nom} - {stock.quantite || stock.quantite_disponible || stock.stock_disponible || 0} {stock.produit_unite || ""}
                      </option>
                    ))}
                  </select>
                  </div>
                  <div className="produit-field">
                    <label htmlFor={`quantite-${index}`}>{t.quantity}</label>
                  <input
                      id={`quantite-${index}`}
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder={t.quantity}
                    value={produit.quantite_utilisee || ""}
                    onChange={(e) => handleProduitChange(index, "quantite_utilisee", e.target.value)}
                    className="quantite-input"
                  />
                  </div>
                  <div className="produit-field">
                    <label htmlFor={`notes-${index}`}>{t.notesOptional}</label>
                  <input
                      id={`notes-${index}`}
                    type="text"
                      placeholder={t.notesOptional}
                    value={produit.notes || ""}
                    onChange={(e) => handleProduitChange(index, "notes", e.target.value)}
                    className="notes-input"
                  />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveProduit(index)}
                    className="remove-produit-button"
                    aria-label="Supprimer ce produit"
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddProduit}
                className="add-produit-button"
              >
                <FaPlus />
                <span>{t.addProduct}</span>
              </button>
            </div>
          </div>

          <div className="form full-width">
            <label htmlFor="description">{t.description}</label>
            <textarea 
              id="description"
              name="description" 
              value={formData.description || ""} 
              onChange={handleChange}
              placeholder={t.description}
              rows="3"
            />
          </div>

          <div className="form">
            <label htmlFor="prochaine_intervention">{t.nextIntervention}</label>
            <input 
              type="date" 
              id="prochaine_intervention"
              name="prochaine_intervention" 
              value={formData.prochaine_intervention || ""} 
              onChange={handleChange} 
            />
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="actions">
          <button
            type="submit"
            className="save-but"
            disabled={selectedAnimaux.length === 0 || submitting}
          >
            <FaCheck />
            <span>{submitting ? t.saving : t.confirm}</span>
          </button>
          <button
            type="button"
            className="cancel-but"
            onClick={() => navigate("/sante")}
            disabled={submitting}
          >
            <FaTimes />
            <span>Annuler</span>
          </button>
        </div>
      </form>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}
